import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
import { BookOpen, Plus, Edit, Trash2, Save, Eye, Tag, Search, Calendar, Image, ChevronDown, ExternalLink, FileText, X, RefreshCw, Sparkles, Loader2 } from 'lucide-react';

const BlogTab = ({ token }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPost, setSelectedPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiTopic, setAiTopic] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    featured_image: '',
    featured_image_alt: '',
    author: 'Apollo Shades',
    category: 'News',
    tags: [],
    status: 'draft',
    meta_title: '',
    meta_description: '',
    meta_keywords: ''
  });

  useEffect(() => {
    fetchPosts();
    fetchCategories();
  }, []);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/blog/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to fetch posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/blog/categories`);
      setCategories(response.data || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const handleSelectPost = async (slug) => {
    try {
      const response = await axios.get(`${API_URL}/admin/blog/posts/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedPost(response.data);
      setFormData({
        title: response.data.title || '',
        slug: response.data.slug || '',
        excerpt: response.data.excerpt || '',
        content: response.data.content || '',
        featured_image: response.data.featured_image || '',
        featured_image_alt: response.data.featured_image_alt || '',
        author: response.data.author || 'Apollo Shades',
        category: response.data.category || 'News',
        tags: response.data.tags || [],
        status: response.data.status || 'draft',
        meta_title: response.data.meta_title || '',
        meta_description: response.data.meta_description || '',
        meta_keywords: response.data.meta_keywords || ''
      });
      setIsCreating(false);
    } catch (error) {
      toast.error('Failed to load post');
    }
  };

  const handleNewPost = () => {
    setSelectedPost(null);
    setIsCreating(true);
    setFormData({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      featured_image: '',
      featured_image_alt: '',
      author: 'Apollo Shades',
      category: 'News',
      tags: [],
      status: 'draft',
      meta_title: '',
      meta_description: '',
      meta_keywords: ''
    });
  };

  const handleAiGenerate = async () => {
    if (!aiTopic.trim()) { toast.error('Enter a topic'); return; }
    setAiGenerating(true);
    try {
      const res = await axios.post(`${API_URL}/admin/blog/generate`, { topic: aiTopic, tone: 'professional' }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data;
      setFormData(prev => ({
        ...prev,
        title: d.title || prev.title,
        content: d.content || prev.content,
        excerpt: d.excerpt || prev.excerpt,
        slug: d.suggested_slug || prev.slug,
        meta_title: d.meta_title || prev.meta_title,
        meta_description: d.meta_description || prev.meta_description,
        meta_keywords: d.keywords || prev.meta_keywords,
      }));
      setShowAiPanel(false);
      setAiTopic('');
      toast.success('Blog content generated! Review and edit as needed.');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'AI generation failed');
    } finally { setAiGenerating(false); }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      if (isCreating) {
        await axios.post(`${API_URL}/admin/blog/posts`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post created!');
      } else {
        await axios.put(`${API_URL}/admin/blog/posts/${selectedPost.slug}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Post updated!');
      }
      fetchPosts();
      setSelectedPost(null);
      setIsCreating(false);
    } catch (error) {
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slug) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API_URL}/admin/blog/posts/${slug}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not published';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric'
    });
  };

  if (loading) {
    return <div className="flex items-center justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-600"></div></div>;
  }

  // List View
  if (!selectedPost && !isCreating) {
    return (
      <div>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold">Blog Posts</h3>
            <p className="text-sm text-neutral-500">Create and manage blog articles</p>
          </div>
          <Button onClick={handleNewPost} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" /> New Post
          </Button>
        </div>

        {posts.length === 0 ? (
          <div className="text-center py-16 bg-neutral-50 rounded-lg">
            <BookOpen className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">No blog posts yet</h3>
            <p className="text-neutral-600 mb-4">Create your first blog post to share news and insights.</p>
            <Button onClick={handleNewPost} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" /> Create First Post
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {posts.map((post) => (
              <div
                key={post.slug}
                className="flex items-center justify-between p-4 border border-neutral-200 rounded-lg hover:border-amber-300 transition-all"
              >
                <div className="flex items-center gap-4 flex-1 cursor-pointer" onClick={() => handleSelectPost(post.slug)}>
                  {post.featured_image ? (
                    <img src={post.featured_image} alt="" className="w-16 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-16 h-12 bg-neutral-100 rounded flex items-center justify-center">
                      <FileText className="w-5 h-5 text-neutral-400" />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{post.title}</p>
                    <div className="flex items-center gap-3 text-sm text-neutral-500">
                      <span className={`px-2 py-0.5 rounded text-xs ${post.status === 'published' ? 'bg-green-100 text-green-700' : post.status === 'draft' ? 'bg-yellow-100 text-yellow-700' : 'bg-neutral-100 text-neutral-600'}`}>
                        {post.status}
                      </span>
                      <span>{post.category}</span>
                      <span>{formatDate(post.published_at)}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => handleSelectPost(post.slug)} className="p-2 hover:bg-neutral-100 rounded">
                    <Edit className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(post.slug)} className="p-2 hover:bg-red-50 text-red-500 rounded">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Edit/Create View
  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <button onClick={() => { setSelectedPost(null); setIsCreating(false); }} className="p-2 hover:bg-neutral-100 rounded-lg">
            <X className="w-5 h-5" />
          </button>
          <div>
            <h3 className="text-lg font-semibold">{isCreating ? 'New Post' : 'Edit Post'}</h3>
            <p className="text-sm text-neutral-500">{isCreating ? 'Create a new blog article' : formData.slug}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={formData.status}
            onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
            className="border rounded-lg px-3 py-2 text-sm"
          >
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="archived">Archived</option>
          </select>
          <Button onClick={handleSave} disabled={saving} className="btn-primary">
            {saving ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {formData.status === 'published' ? 'Publish' : 'Save Draft'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-4">
          {/* AI Content Generator */}
          <div className="border border-purple-200 rounded-lg overflow-hidden bg-purple-50/30" data-testid="ai-blog-generator">
            <button
              onClick={() => setShowAiPanel(!showAiPanel)}
              className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-purple-50 transition-colors"
              data-testid="ai-generate-toggle"
            >
              <Sparkles className="w-4 h-4 text-purple-600" />
              <span className="text-sm font-medium text-purple-700">Generate with AI</span>
              <ChevronDown className={`w-3 h-3 text-purple-400 ml-auto transition-transform ${showAiPanel ? 'rotate-180' : ''}`} />
            </button>
            {showAiPanel && (
              <div className="px-4 pb-4 space-y-3 border-t border-purple-200">
                <div className="pt-3">
                  <Label className="text-xs text-purple-700">Topic or idea</Label>
                  <Input
                    value={aiTopic}
                    onChange={e => setAiTopic(e.target.value)}
                    placeholder="e.g., Benefits of motorized roller shades for smart homes"
                    className="mt-1 border-purple-200 focus:border-purple-400"
                    data-testid="ai-topic-input"
                  />
                </div>
                <Button
                  onClick={handleAiGenerate}
                  disabled={aiGenerating || !aiTopic.trim()}
                  className="bg-purple-600 hover:bg-purple-700 text-white w-full"
                  data-testid="ai-generate-btn"
                >
                  {aiGenerating ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Generating...</> : <><Sparkles className="w-4 h-4 mr-2" /> Generate Blog Post & SEO</>}
                </Button>
                <p className="text-[10px] text-purple-400 text-center">AI will fill in title, content, excerpt, slug, and SEO fields</p>
              </div>
            )}
          </div>

          <div>
            <Label>Title</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Post title..."
              className="mt-1 text-lg"
            />
          </div>

          <div>
            <Label>Excerpt / Summary</Label>
            <Textarea
              value={formData.excerpt}
              onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
              placeholder="A brief summary of the post..."
              rows={2}
              className="mt-1"
            />
          </div>

          <div>
            <Label>Content</Label>
            <Textarea
              value={formData.content}
              onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
              placeholder="Write your blog post content here..."
              rows={15}
              className="mt-1"
            />
            <p className="text-xs text-neutral-500 mt-1">Supports plain text. Line breaks will be preserved.</p>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Featured Image */}
          <div className="border rounded-lg p-4">
            <Label className="mb-2 block">Featured Image</Label>
            <Input
              value={formData.featured_image}
              onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
              placeholder="Image URL..."
              className="mb-2"
            />
            <Input
              value={formData.featured_image_alt}
              onChange={(e) => setFormData(prev => ({ ...prev, featured_image_alt: e.target.value }))}
              placeholder="Alt text for image..."
              className="text-sm"
            />
            {formData.featured_image && (
              <img src={formData.featured_image} alt="" className="w-full h-32 object-cover rounded mt-2" />
            )}
          </div>

          {/* Category & Author */}
          <div className="border rounded-lg p-4 space-y-3">
            <div>
              <Label>Category</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
                className="w-full border rounded-lg px-3 py-2 mt-1"
              >
                {categories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Author</Label>
              <Input
                value={formData.author}
                onChange={(e) => setFormData(prev => ({ ...prev, author: e.target.value }))}
                className="mt-1"
              />
            </div>
            <div>
              <Label>URL Slug</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                placeholder="auto-generated-from-title"
                className="mt-1 text-sm"
              />
            </div>
          </div>

          {/* SEO */}
          <div className="border rounded-lg p-4 space-y-3">
            <p className="font-medium text-sm">SEO Settings</p>
            <div>
              <Label className="text-xs">Meta Title</Label>
              <Input
                value={formData.meta_title}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_title: e.target.value }))}
                placeholder="SEO title..."
                className="mt-1 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Meta Description</Label>
              <Textarea
                value={formData.meta_description}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                placeholder="SEO description..."
                rows={2}
                className="mt-1 text-sm"
              />
            </div>
            <div>
              <Label className="text-xs">Keywords</Label>
              <Input
                value={formData.meta_keywords}
                onChange={(e) => setFormData(prev => ({ ...prev, meta_keywords: e.target.value }))}
                placeholder="keyword1, keyword2..."
                className="mt-1 text-sm"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};


export default BlogTab;
